<template>
   <LoadingPage v-if="isLoading"></LoadingPage>
   <div class="proposals-page proposals-page-v2 p-3">
      <!-- ── Modern Page Header (Matching Leads V2) ── -->
      <div class="ms-page-header">
         <div class="ms-page-header__content d-flex justify-content-between align-items-center flex-wrap gap-3 w-100">
            <div class="d-flex align-items-center gap-3">
               <div class="ms-page-header__icon">
                  <i class="fa-solid fa-file-contract"></i>
               </div>
               <div>
                  <p class="ms-page-header__breadcrumb">Sales Pipeline <span class="ms-breadcrumb-sep">/</span> Proposals</p>
                  <div class="d-flex align-items-center flex-wrap gap-2 mt-1">
                     <h1 class="ms-page-header__title">Proposals Workspace</h1>
                     <span class="badge bg-primary-soft text-primary rounded-pill px-2 py-1 fs-xs fw-bold">
                        {{ pagination.totalItems }} Total
                     </span>
                  </div>
               </div>
            </div>

            <!-- Header Action CTAs -->
            <div class="d-flex align-items-center gap-2 flex-wrap">
               <button 
                  v-if="!$store.state.auth.isConsultant" 
                  type="button" 
                  class="action-btn-circle action-btn-circle--primary"
                  @click="refreshDraftPrices()" 
                  :disabled="isLoading"
                  title="Refresh item prices and sync Auth-based items for draft proposals"
                  aria-label="Refresh Prices"
               >
                  <i class="fa-solid fa-arrows-rotate"></i>
               </button>

               <button 
                  type="button" 
                  class="action-btn-circle action-btn-circle--primary"
                  @click="openCreateProposalModal()" 
                  :disabled="isLoading"
                  title="Create Proposal"
                  aria-label="Create Proposal"
               >
                  <i class="fa-solid fa-plus"></i>
               </button>
            </div>
         </div>
      </div>
      <!-- ── Main Workspace Card ── -->
      <div class="card modern-table-card border-0 shadow-sm">
         <div class="card-body p-3">

            <!-- ── Quick Tabs & Segmented Filters (Matching Leads V2) ── -->
            <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 pb-3 border-bottom mb-3">
               <div class="modern-tabs">
                  <button type="button" class="modern-tab" :class="{ active: activeQuickTab === 'all' }" @click="setQuickTab('all')">
                     <i class="fa-solid fa-list me-1"></i> All Proposals
                  </button>
                  <button type="button" class="modern-tab" :class="{ active: activeQuickTab === 'sent' }" @click="setQuickTab('sent')">
                     <i class="fa-solid fa-paper-plane me-1"></i> Out for Review
                     <span v-if="kpiMetrics.sentCount > 0" class="badge bg-teal-soft text-teal rounded-pill ms-1 fs-xs">{{ kpiMetrics.sentCount }}</span>
                  </button>
                  <button type="button" class="modern-tab" :class="{ active: activeQuickTab === 'accepted' }" @click="setQuickTab('accepted')">
                     <i class="fa-solid fa-circle-check me-1"></i> Accepted
                     <span v-if="kpiMetrics.acceptedCount > 0" class="badge bg-emerald-soft text-emerald rounded-pill ms-1 fs-xs">{{ kpiMetrics.acceptedCount }}</span>
                  </button>
                  <button type="button" class="modern-tab" :class="{ active: activeQuickTab === 'draft' }" @click="setQuickTab('draft')">
                     <i class="fa-solid fa-pen-ruler me-1"></i> Drafts
                     <span v-if="kpiMetrics.draftCount > 0" class="badge bg-indigo-soft text-indigo rounded-pill ms-1 fs-xs">{{ kpiMetrics.draftCount }}</span>
                  </button>
                  <button type="button" class="modern-tab" :class="{ active: activeQuickTab === 'expired' }" @click="setQuickTab('expired')">
                     <i class="fa-solid fa-calendar-xmark me-1"></i> Expired
                     <span v-if="kpiMetrics.expiredCount > 0" class="badge bg-amber-soft text-amber rounded-pill ms-1 fs-xs">{{ kpiMetrics.expiredCount }}</span>
                  </button>
               </div>

               <!-- Pagination & View Controls -->
               <div class="d-flex align-items-center gap-2 ms-auto">
                  <div class="d-flex align-items-center gap-1">
                     <span class="text-muted fs-sm">Rows:</span>
                     <select v-model="pagination.pageSize" class="form-select form-select-sm modern-select" style="width: 70px;">
                        <option v-for="row in rowsPerPageList" :value="row.value" :key="row.value">{{ row.text }}</option>
                     </select>
                  </div>

                  <div class="pagination-modern">
                     <button @click="previousPage()" :disabled="pagination.page === 1" class="pagination-arrow" title="Previous Page">
                        <i class="fa-solid fa-chevron-left"></i>
                     </button>
                     <span class="pagination-label">
                        <strong>{{ paginationLabel }}</strong> of {{ pagination.totalItems }}
                     </span>
                     <button @click="nextPage()" :disabled="pagination.page === pagination.totalPages || pagination.totalItems === 0" class="pagination-arrow" title="Next Page">
                        <i class="fa-solid fa-chevron-right"></i>
                     </button>
                  </div>
               </div>
            </div>

            <!-- ── Search & Filter Toolbar ── -->
            <div class="d-flex align-items-center flex-wrap gap-2 mb-3">
               <!-- Search Input -->
               <div class="modern-search-wrapper" style="min-width: 260px; max-width: 360px;">
                  <i class="fa-solid fa-magnifying-glass search-icon"></i>
                  <input 
                     v-model.trim="search" 
                     type="text" 
                     class="form-control form-control-sm modern-search-input"
                     placeholder="Search organisation or title..." 
                     @keyup="onSearch()" 
                  />
                  <button v-if="search" type="button" class="btn-clear-search" @click="clearSearch()">
                     <i class="fa-solid fa-xmark"></i>
                  </button>
               </div>

               <!-- Status Filter Dropdown -->
               <div class="dropdown">
                  <button 
                     type="button" 
                     class="btn btn-sm modern-filter-btn dropdown-toggle"
                     data-bs-toggle="dropdown" 
                     aria-expanded="false"
                  >
                     <i class="fa-solid fa-sliders me-1 text-teal"></i>
                     <span>Status</span>
                     <span v-if="statusFilter.length > 0" class="filter-count-badge ms-1">
                        {{ statusFilter.length }}
                     </span>
                  </button>
                  <div class="dropdown-menu p-3 shadow-lg border-0" style="min-width: 240px; border-radius: 12px;" @click.stop>
                     <div class="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                        <span class="fw-bold fs-xs text-muted text-uppercase">Filter Status</span>
                        <button v-if="statusFilter.length > 0" type="button" class="btn btn-link btn-xs p-0 text-teal text-decoration-none" @click="statusFilter = []">Reset</button>
                     </div>
                     <div class="d-flex flex-column gap-2" style="max-height: 240px; overflow-y: auto;">
                        <label v-for="opt in statusOptions" :key="opt.value" class="modern-checkbox-label">
                           <input type="checkbox" :value="opt.value" v-model="statusFilter">
                           <span class="custom-chk"></span>
                           <span class="status-dot ms-2" :class="getProposalStatusDotClass(opt.value)"></span>
                           <span class="label-text ms-2">{{ opt.text }}</span>
                        </label>
                     </div>
                  </div>
               </div>

               <!-- Organisation Filter Dropdown -->
               <div class="dropdown">
                  <button 
                     type="button" 
                     class="btn btn-sm modern-filter-btn dropdown-toggle"
                     data-bs-toggle="dropdown" 
                     aria-expanded="false"
                  >
                     <i class="fa-solid fa-building me-1 text-teal"></i>
                     <span>Organisation</span>
                     <span v-if="leadFilter.length > 0" class="filter-count-badge ms-1">
                        {{ leadFilter.length }}
                     </span>
                  </button>
                  <div class="dropdown-menu p-3 shadow-lg border-0" style="min-width: 280px; border-radius: 12px;" @click.stop>
                     <div class="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                        <span class="fw-bold fs-xs text-muted text-uppercase">Organisations</span>
                        <button v-if="leadFilter.length > 0" type="button" class="btn btn-link btn-xs p-0 text-teal text-decoration-none" @click="leadFilter = []">Reset</button>
                     </div>
                     <div class="d-flex flex-column gap-2" style="max-height: 250px; overflow-y: auto;">
                        <label v-for="(l, idx) in leads" :key="l.id || idx" class="modern-checkbox-label">
                           <input type="checkbox" :value="l.id" v-model="leadFilter">
                           <span class="custom-chk"></span>
                           <span class="label-text ms-2 text-truncate" style="max-width: 210px;">
                              {{ l.organisation_name || `${l.first_name} ${l.last_name}` }}
                           </span>
                        </label>
                     </div>
                  </div>
               </div>

               <!-- Active Filter Chips -->
               <div v-if="isFiltered" class="d-flex align-items-center flex-wrap gap-1 ms-1">
                  <span v-for="val in statusFilter" :key="'status-' + val" class="filter-chip">
                     <span class="status-dot-xs me-1" :class="getProposalStatusDotClass(val)"></span>
                     {{ getStatusText(parseInt(val, 10)) }}
                     <i class="fa-solid fa-xmark ms-1 cursor-pointer" @click="removeStatusFilter(val)"></i>
                  </span>
                  <span v-for="val in leadFilter" :key="'lead-' + val" class="filter-chip">
                     <i class="fa-solid fa-building me-1 opacity-75"></i>
                     {{ getLeadDisplayName(val) }}
                     <i class="fa-solid fa-xmark ms-1 cursor-pointer" @click="removeLeadFilter(val)"></i>
                  </span>
                  <button type="button" class="btn btn-link btn-xs p-0 text-teal text-decoration-none ms-1 fw-bold fs-xs" @click="clearAllFiltersAndSearch()">
                     Reset All
                  </button>
               </div>
            </div>

            <!-- ── Main Proposals Table ── -->
            <div class="modern-table-wrapper table-responsive">
               <table class="table modern-table mb-0">
                  <thead>
                     <tr>
                        <th scope="col" style="width: 85px;" class="text-nowrap sortable-th" :class="{ 'sorted-th': isSorted('proposal_version') }" @click="toggleSort('proposal_version', 'desc')" title="Sort by Version">
                           <div class="d-flex align-items-center gap-1">
                              <span>Version</span>
                              <i :class="getSortIcon('proposal_version')"></i>
                           </div>
                        </th>
                        <th scope="col" style="width: 70px; text-align: center;" class="text-nowrap sortable-th" :class="{ 'sorted-th': isSorted('created_by') }" @click="toggleSort('created_by', 'asc')" title="Sort by Owner">
                           <div class="d-flex align-items-center justify-content-center gap-1">
                              <span>Owner</span>
                              <i :class="getSortIcon('created_by')"></i>
                           </div>
                        </th>
                        <th scope="col" class="text-nowrap sortable-th" :class="{ 'sorted-th': isSorted('proposal_title') }" @click="toggleSort('proposal_title', 'asc')" title="Sort by Organisation & Proposal">
                           <div class="d-flex align-items-center gap-1">
                              <span>Organisation & Proposal</span>
                              <i :class="getSortIcon('proposal_title')"></i>
                           </div>
                        </th>
                        <th scope="col" class="text-end text-nowrap sortable-th" style="width: 120px;" :class="{ 'sorted-th': isSorted('proposal_total_amount') }" @click="toggleSort('proposal_total_amount', 'desc')" title="Sort by Total Value">
                           <div class="d-flex align-items-center justify-content-end gap-1">
                              <span>Total Value</span>
                              <i :class="getSortIcon('proposal_total_amount')"></i>
                           </div>
                        </th>
                        <th scope="col" class="text-end text-nowrap sortable-th" style="width: 130px;" :class="{ 'sorted-th': isSorted('proposal_accepted_amount') }" @click="toggleSort('proposal_accepted_amount', 'desc')" title="Sort by Accepted Value">
                           <div class="d-flex align-items-center justify-content-end gap-1">
                              <span>Accepted Value</span>
                              <i :class="getSortIcon('proposal_accepted_amount')"></i>
                           </div>
                        </th>
                        <th scope="col" style="width: 130px; text-align: center;" class="text-nowrap sortable-th" :class="{ 'sorted-th': isSorted('proposal_status') }" @click="toggleSort('proposal_status', 'asc')" title="Sort by Status">
                           <div class="d-flex align-items-center justify-content-center gap-1">
                              <span>Status</span>
                              <i :class="getSortIcon('proposal_status')"></i>
                           </div>
                        </th>
                        <th scope="col" style="width: 110px;" class="text-nowrap sortable-th" :class="{ 'sorted-th': isSorted('created_at') }" @click="toggleSort('created_at', 'desc')" title="Sort by Created Date">
                           <div class="d-flex align-items-center gap-1">
                              <span>Created</span>
                              <i :class="getSortIcon('created_at')"></i>
                           </div>
                        </th>
                        <th scope="col" style="width: 110px;" class="text-nowrap sortable-th" :class="{ 'sorted-th': isSorted('sent_at') }" @click="toggleSort('sent_at', 'desc')" title="Sort by Sent Date">
                           <div class="d-flex align-items-center gap-1">
                              <span>Sent Date</span>
                              <i :class="getSortIcon('sent_at')"></i>
                           </div>
                        </th>
                        <th scope="col" style="width: 110px;" class="text-nowrap sortable-th" :class="{ 'sorted-th': isSorted('expiry_date') }" @click="toggleSort('expiry_date', 'desc')" title="Sort by Expiry Date">
                           <div class="d-flex align-items-center gap-1">
                              <span>Expiry</span>
                              <i :class="getSortIcon('expiry_date')"></i>
                           </div>
                        </th>
                        <th scope="col" style="width: 110px;" class="text-nowrap sortable-th" :class="{ 'sorted-th': isSorted('accepted_at') }" @click="toggleSort('accepted_at', 'desc')" title="Sort by Accepted Date">
                           <div class="d-flex align-items-center gap-1">
                              <span>Accepted</span>
                              <i :class="getSortIcon('accepted_at')"></i>
                           </div>
                        </th>
                        <th scope="col" style="width: 140px; text-align: right;" class="text-nowrap">Actions</th>
                     </tr>
                  </thead>

                  <TableEmptyState
                     v-if="pagination.totalItems <= 0"
                     :colspan="11"
                     icon="fa-solid fa-file-contract"
                     title="No Proposals Found"
                     description="There are currently no proposals matching your criteria. Create a new proposal to start presenting quotes to clients."
                     action-text="Create Proposal"
                     action-icon="fa-solid fa-plus"
                     :is-filtered="isFiltered"
                     filtered-title="No Matching Proposals"
                     filtered-description="No proposals match your current search query or active filter criteria. Try adjusting or clearing your filters."
                     clear-filters-text="Reset Filters & Search"
                     @action="openCreateProposalModal"
                     @clear-filters="clearAllFiltersAndSearch"
                  />

                  <tbody v-else>
                     <tr 
                        v-for="proposal in proposals" 
                        :key="proposal.id" 
                        class="modern-row" 
                        @click="openProposalInNewTab(proposal.id)"
                     >
                        <!-- Version Column (Clean Soft Pill Badge without Icon) -->
                        <td 
                           :style="proposal.shared_users && proposal.shared_users.length > 0 ? 'border-left: 3.5px solid #00A0C1; padding-left: 14px;' : ''"
                           :title="proposal.shared_users && proposal.shared_users.length > 0 ? getSharedEmailsTooltip(proposal.shared_users) : null"
                        >
                           <div class="d-flex align-items-center">
                              <span 
                                 class="spark-pill-badge"
                                 :class="getVersionBadgeClass(proposal.proposal_version)"
                                 :title="getProposalTooltip(proposal)"
                              >
                                 {{ proposal.proposal_version || 'v1.0' }}
                              </span>
                           </div>
                        </td>

                        <!-- Owner / Creator -->
                        <td style="text-align: center;">
                           <span 
                              class="user-initials-icon-sm"
                              :style="{ backgroundColor: getColorFromName(getVertexAuthUser(proposal.created_by)) }"
                              :title="getVertexAuthUser(proposal.created_by)"
                           >
                              {{ retrieveUserIcon(getVertexAuthUser(proposal.created_by)) }}
                           </span>
                        </td>

                        <!-- Organisation & Title -->
                        <td>
                           <div class="d-flex flex-column">
                              <div class="d-flex align-items-center flex-wrap gap-2">
                                 <span class="proposal-org-name">
                                    {{ getOrganisationDisplayName(proposal) }}
                                 </span>
                                 <span 
                                    v-if="proposal.auth_organisation && proposal.auth_organisation.name && !proposal.is_auth_sync" 
                                    class="spark-pill-badge spark-pill-badge--linked" 
                                    :title="'Original Lead Name: ' + (proposal.Lead?.original_organisation_name || proposal.Lead?.organisation_name || 'N/A')"
                                 >
                                    <i class="fa-solid fa-link"></i> Linked
                                 </span>
                              </div>

                              <div class="proposal-sub-title text-muted mt-1">
                                 <i class="fa-solid fa-file-lines me-1 opacity-75"></i>
                                 {{ proposal.proposal_title || 'Untitled Proposal' }}
                              </div>

                              <div 
                                 v-if="proposal.Lead && proposal.Lead.original_organisation_name && proposal.Lead.original_organisation_name !== '' && proposal.Lead.original_organisation_name !== getOrganisationDisplayName(proposal)" 
                                 class="text-muted fst-italic mt-1 fs-xs"
                              >
                                 <i class="fa-solid fa-clock-rotate-left me-1"></i>Formerly: <strong>{{ proposal.Lead.original_organisation_name }}</strong>
                              </div>
                           </div>
                        </td>

                        <!-- Total Amount -->
                        <td class="text-end">
                           <span class="fw-bold text-dark fs-xs">{{ formatCurrency(proposal.proposal_total_amount) }}</span>
                        </td>

                        <!-- Accepted Amount -->
                        <td class="text-end">
                           <span 
                              class="fw-bold fs-xs"
                              :class="proposal.proposal_accepted_amount > 0 ? 'text-emerald' : 'text-muted'"
                           >
                              {{ formatCurrency(proposal.proposal_accepted_amount) }}
                           </span>
                        </td>

                        <!-- Status Pill Badge (Single Soft Pill Badge) -->
                        <td style="text-align: center;">
                           <span 
                              class="spark-pill-badge"
                              :class="getProposalStatusBadgeClass(proposal.proposal_status)"
                           >
                              <i :class="getProposalStatusIcon(proposal.proposal_status)"></i>
                              {{ getStatusText(proposal.proposal_status) }}
                           </span>
                        </td>

                        <!-- Created Date -->
                        <td>
                           <span class="fs-xs text-secondary text-nowrap">{{ formatDate(proposal.created_at) }}</span>
                        </td>

                        <!-- Sent Date (Simplified clean text) -->
                        <td>
                           <span 
                              v-if="isValidDate(proposal.sent_at)" 
                              class="fs-xs text-teal fw-medium text-nowrap"
                              :title="formatTimestamp(proposal.sent_at)"
                           >
                              {{ formatDate(proposal.sent_at) }}
                           </span>
                           <span v-else class="text-muted fs-xs">-</span>
                        </td>

                        <!-- Expiry Date (Simplified clean text) -->
                        <td>
                           <span 
                              v-if="isValidDate(proposal.expiry_date)" 
                              class="fs-xs text-nowrap"
                              :class="isProposalExpired(proposal) ? 'text-danger fw-semibold' : 'text-secondary'"
                              :title="formatTimestamp(proposal.expiry_date)"
                           >
                              {{ formatDate(proposal.expiry_date) }}
                           </span>
                           <span v-else class="text-muted fs-xs">-</span>
                        </td>

                        <!-- Accepted Date (Simplified clean text) -->
                        <td>
                           <span 
                              v-if="isValidDate(proposal.accepted_at)" 
                              class="fs-xs text-emerald fw-medium text-nowrap"
                              :title="formatTimestamp(proposal.accepted_at)"
                           >
                              {{ formatDate(proposal.accepted_at) }}
                           </span>
                           <span v-else class="text-muted fs-xs">-</span>
                        </td>

                        <!-- Action Column (Circular Icon Buttons + Circular 3-Dot More Actions) -->
                        <td style="text-align: right;" @click.stop>
                           <div class="d-flex align-items-center justify-content-end gap-2">
                              <!-- 1) Primary CTA: Send Proposal -->
                              <button 
                                 v-if="!$store.state.auth.isGeneralUser && $store.getters['auth/canSendProposal']" 
                                 type="button" 
                                 class="action-btn-circle action-btn-circle--send"
                                 :disabled="proposal.proposal_status == 3 || proposal.proposal_status == 4 || proposal.proposal_status == 6"
                                 :title="getSendButtonTitle(proposal)"
                                 @click="onButtonClick('send', proposal); openSendModal();"
                              >
                                 <i class="fa-solid fa-paper-plane"></i>
                              </button>

                              <!-- 2) Secondary CTA: Edit Proposal -->
                              <button 
                                 type="button" 
                                 class="action-btn-circle action-btn-circle--edit"
                                 title="Edit Proposal"
                                 @click="onButtonClick('view-edit', proposal.id)"
                              >
                                 <i class="fa-solid fa-pen-to-square"></i>
                              </button>

                              <!-- 3) 3-Dot More Actions Menu -->
                              <div class="dropdown">
                                 <button type="button" class="btn-more-actions" data-bs-toggle="dropdown" aria-expanded="false" title="More Actions">
                                    <i class="fa-solid fa-ellipsis-vertical"></i>
                                 </button>
                                 <ul class="dropdown-menu dropdown-menu-end shadow-lg border-0 py-2 modern-action-dropdown">

                                    <!-- View Client Landing Page -->
                                    <li>
                                       <a class="dropdown-item d-flex align-items-center gap-2 cursor-pointer"
                                          @click="onButtonClick('view-client', proposal)">
                                          <i class="fa-solid fa-arrow-up-right-from-square text-info"></i> View Client Page
                                       </a>
                                    </li>

                                    <!-- Linked Contracts -->
                                    <li>
                                       <a class="dropdown-item d-flex align-items-center gap-2"
                                          :class="{ 'disabled text-muted opacity-50': getContractCount(proposal.id) === 0, 'cursor-pointer': getContractCount(proposal.id) > 0 }"
                                          @click="getContractCount(proposal.id) > 0 ? onButtonClick('view-contracts', proposal) : null">
                                          <i class="fa-solid fa-file-signature text-primary"></i>
                                          <span>Linked Contracts</span>
                                          <span v-if="getContractCount(proposal.id) > 0" class="badge bg-primary-soft text-primary rounded-pill ms-auto fs-xs">
                                             {{ getContractCount(proposal.id) }}
                                          </span>
                                       </a>
                                    </li>

                                    <!-- Activity History -->
                                    <li>
                                       <a class="dropdown-item d-flex align-items-center gap-2 cursor-pointer"
                                          @click="onButtonClick('activity-log', proposal.id)">
                                          <i class="fa-solid fa-clock-rotate-left text-secondary"></i> Activity History
                                       </a>
                                    </li>

                                    <li><hr class="dropdown-divider my-1"></li>

                                    <!-- Delete Proposal (Only if Draft) -->
                                    <li v-if="!$store.state.auth.isGeneralUser">
                                       <a class="dropdown-item d-flex align-items-center gap-2 text-danger"
                                          :class="{ 'disabled text-muted opacity-50': proposal.proposal_status != 0, 'cursor-pointer': proposal.proposal_status == 0 }"
                                          @click="proposal.proposal_status == 0 ? onButtonClick('delete', proposal.id) : null">
                                          <i class="fa-solid fa-trash-can"></i> Delete Proposal
                                       </a>
                                    </li>
                                 </ul>
                              </div>
                           </div>
                        </td>
                     </tr>
                  </tbody>
               </table>
            </div>

         </div>
      </div>

      <!-- ── Modals ── -->

      <!-- Send Proposal Modal -->
      <div 
         class="modal fade" 
         id="sendProposalModal" 
         tabindex="-1" 
         data-bs-keyboard="false" 
         data-bs-backdrop="static" 
         ref="sendForm"
         style="top: 2%;"
      >
         <div ref="modalSendFormDialogRef" class="modal-dialog modal-dialog-scrollable modal-lg">
            <div class="modal-content shadow-lg border-0" style="border-radius: 16px; background-color: #fff;">
               <div class="modal-header border-0 pb-0 justify-content-end" style="background-color: transparent;">
                  <button ref="closeSendFormModal" type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
               </div>
               <div class="modal-body p-4 pt-0">
                  <SendForm 
                     ref="sendProposalForm" 
                     :initialSendTo="proposal_client_email" 
                     :proposalID="proposal_id"
                     :hasEditor="false" 
                     :initialProposalTitle="proposal_title"
                     @proposalSentSuccess="init(); $refs.closeSendFormModal.click(); getProposalsList()" 
                  />
               </div>
            </div>
         </div>
      </div>

      <!-- Linked Contracts Selection Modal -->
      <div v-if="isContractModalOpen" class="contract-overlay" @click.self="isContractModalOpen = false">
         <div class="contract-select-modal">
            <div class="contract-select-header">
               <h5 class="contract-select-title">Linked Contracts</h5>
               <button @click="isContractModalOpen = false" class="contract-select-close">&times;</button>
            </div>
            <div class="contract-select-body">
               <div 
                  v-for="contract in selectedProposalContracts" 
                  :key="contract.id" 
                  class="contract-card"
                  @click="openContractView(contract)"
               >
                  <div class="contract-card-info">
                     <p class="contract-card-name">{{ contract.contract_title || 'Untitled Contract' }}</p>
                     <p class="contract-card-detail">
                        <strong>Status:</strong> 
                        <span :style="{ color: getContractStatusColor(contract.contract_status) }">
                           {{ getContractStatusText(contract.contract_status) }}
                        </span>
                     </p>
                     <p class="contract-card-detail" v-if="contract.contract_version">
                        <strong>Version:</strong> {{ contract.contract_version }}
                     </p>
                     <p class="contract-card-detail">
                        <strong>Created:</strong> {{ formatDate(contract.created_at) }}
                     </p>
                  </div>
               </div>
               <div v-if="selectedProposalContracts.length === 0" class="contract-no-results">
                  <p>No contracts found.</p>
               </div>
            </div>
         </div>
      </div>

      <!-- Activity Log Modal -->
      <div class="modal fade" id="proposalActivityModal" ref="activityLogModal" tabindex="-1">
         <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg modal-blur">
            <div class="modal-content modern-modal-content">
               <div class="modern-activity-modal-header">
                  <div class="d-flex align-items-center gap-3">
                     <div class="modal-header-icon-badge">
                        <i class="fa-solid fa-clock-rotate-left text-primary"></i>
                     </div>
                     <div>
                        <h5 class="modal-title mb-0 fw-bold">Proposal Activity Timeline</h5>
                        <p class="modal-subtitle text-muted mb-0 small">
                           Real-time audit log of all events for this proposal
                        </p>
                     </div>
                  </div>
                  <button type="button" class="modern-modal-close-btn" data-bs-dismiss="modal" aria-label="Close">
                     <i class="fa-solid fa-xmark"></i>
                  </button>
               </div>
               <div class="modal-body modern-activity-modal-body p-0">
                  <ProposalActivity ref="proposalActivityRef" />
               </div>
               <div class="modal-footer modern-activity-modal-footer">
                  <button type="button" class="btn btn-light btn-sm fw-semibold rounded-pill px-3" data-bs-dismiss="modal">
                     Close
                  </button>
               </div>
            </div>
         </div>
      </div>

      <!-- Create Proposal Modal -->
      <div 
         class="modal fade modal-blur" 
         id="createProposalModalV2" 
         tabindex="-1" 
         data-bs-keyboard="false" 
         data-bs-backdrop="static"
         ref="createProposalModalRef" 
         style="top: 2%"
      >
         <div class="blur-backdrop"></div>
         <div 
            ref="createProposalModalDialogRef" 
            class="modal-dialog modal-dialog-centered modal-dialog-scrollable"
            :class="[isLeadSelected ? 'modal-custom-width' : 'modal-md-width']"
         >
            <div class="modal-content shadow-2xl border-0" style="border-radius: 18px; border: none; box-shadow: 0 10px 30px rgba(0,0,0,0.25); background-color: #fff; min-height: 600px; max-height: 92vh;">
               <button type="button" class="close-button-custom" @click="closeCreateProposalModal" aria-label="Close">&times;</button>
               <div class="px-4 pt-4 pb-0 text-start">
                  <h5 class="modal-title-custom fw-bold fs-5 text-dark">Create New Proposal</h5>
                  <hr class="mt-3 mb-2 opacity-10">
               </div>
               <div class="modal-body px-4 pb-4 pt-2 animate-pop-in" style="background-color: #fff !important; max-height: calc(90vh - 110px); overflow-y: auto !important;">
                  <VertexProposal :lead_id="selectedLeadID" ref="createProposalRef" @lead-changed="handleLeadChanged" />
               </div>
            </div>
         </div>
      </div>

      <!-- Confirmation Modal -->
      <ConfirmModal
         v-model="showConfirmModal"
         :title="confirmModalTitle"
         :message="confirmModalMessage"
         @confirm="onConfirmAction"
      />
   </div>
</template>

<script>
import moment from "moment";
import { debounce } from '@/plugins/helper.js';
import LoadingPage from '@/components/Utilities/vertex-loading.vue';
import SendForm from '@/components/Proposals/SendForm.vue';
import { Modal } from 'bootstrap';
import ProposalActivity from '@/components/Proposals/ProposalActivity.vue';
import VertexProposal from '@/components/Proposals/VertexProposal.vue';
import TableEmptyState from '@/components/Utilities/TableEmptyState.vue';

export default {
   name: "ProposalsDashboardV2",
   components: {
      LoadingPage,
      SendForm,
      ProposalActivity,
      VertexProposal,
      TableEmptyState
   },

   data() {
      return {
         search: "",
         leads: [],
         proposals: [],
         pagination: {
            pageSize: 10,
            page: 1,
            totalItems: 0,
            totalPages: 1,
         },
         rowsPerPageList: [
            { text: "5", value: 5 },
            { text: "10", value: 10 },
            { text: "20", value: 20 },
            { text: "50", value: 50 },
         ],
         statusFilter: ["0", "1", "2", "3", "5"],
         leadFilter: [],
         activeQuickTab: 'all',
         statusOptions: [
            { text: "Draft", value: "0" },
            { text: "Sent", value: "1" },
            { text: "Resent", value: "2" },
            { text: "Accepted", value: "3" },
            { text: "Declined", value: "4" },
            { text: "Expired", value: "5" },
            { text: "Completed", value: "6" },
         ],
         sorting: {
            field: "created_at",
            order: "desc",
            filter: "none"
         },
         isLoading: false,
         colorPalette: [
            '#00A0C1', '#3b82f6', '#8b5cf6', '#ec4899', '#f97316', 
            '#10b981', '#14b8a6', '#6366f1', '#06b6d4', '#e11d48'
         ],
         proposal_client_email: "",
         proposal_id: "",
         proposal_title: "",
         sendProposalForm: null,
         proposalContractsMap: {},
         selectedProposalContracts: [],
         isContractModalOpen: false,
         vtxUsers: [],
         ws: null,
         showConfirmModal: false,
         confirmModalTitle: '',
         confirmModalMessage: '',
         pendingAction: null,
         pendingActionData: null,
         createProposalModalInstance: null,
         activityLogModalInstance: null,
         selectedLeadID: "",
         isLeadSelected: false,
         kpiMetrics: {
            totalProposals: 0,
            totalValue: 0,
            sentCount: 0,
            sentValue: 0,
            acceptedCount: 0,
            acceptedValue: 0,
            expiredCount: 0,
            draftCount: 0,
         }
      };
   },

   created() {
      this.init();
      this.debounceFn = debounce(() => this.getProposalsList(), 500);
   },

   mounted() {
      this.sendProposalForm = new Modal(this.$refs.sendForm);
      this.activityLogModalInstance = new Modal(this.$refs.activityLogModal);
      this.createProposalModalInstance = new Modal(this.$refs.createProposalModalRef);

      if (this.$refs.createProposalModalRef) {
         this.$refs.createProposalModalRef.addEventListener('hidden.bs.modal', () => {
            if (this.$route.query && (this.$route.query.create || this.$route.query.org_id || this.$route.query.lead_id || this.$route.query.contact_id || this.$route.query.contact_uuid || this.$route.query.contact_email)) {
               this.$router.replace({ path: '/proposals-v2' });
            }
         });
      }

      this.checkRouteForCreateProposal();

      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const token = this.$store?.state?.auth?.accessToken || '';
      const wsUrl = `${wsProtocol}//${window.location.host}/ws` + (token ? `?token=${encodeURIComponent(token)}` : '');

      this.ws = new WebSocket(wsUrl);
      this.ws.onmessage = (event) => {
         try {
            const data = JSON.parse(event.data);
            if (data.event === "price_refresh_completed") {
               this.isLoading = false;
               this.$toast.success("Draft prices and Auth relationships have been fully synced!");
               this.getProposalsList();
               this.fetchKpiMetrics();
            } else if (data.event === "price_refresh_failed") {
               this.isLoading = false;
               this.$toast.error("Background price sync failed.");
            }
         } catch (err) {
            console.error("WS parse error", err);
         }
      };
   },

   beforeUnmount() {
      if (this.ws) {
         this.ws.close();
      }
   },

   computed: {
      isFiltered() {
         return (this.search && this.search.trim().length > 0) ||
                this.statusFilter.length > 0 ||
                this.leadFilter.length > 0 ||
                this.activeQuickTab !== 'all';
      },
      paginationLabel() {
         const start = Math.min((this.pagination.page - 1) * this.pagination.pageSize + 1, this.pagination.totalItems);
         const end = Math.min((this.pagination.page * this.pagination.pageSize), this.pagination.totalItems);
         return start + " - " + end;
      },
   },

   watch: {
      'pagination.pageSize'() {
         this.pagination.page = 1;
         this.getProposalsList();
      },
      'pagination.page'() {
         this.getProposalsList();
      },
      statusFilter: {
         handler() {
            this.pagination.page = 1;
            this.getProposalsList();
         },
         deep: true
      },
      leadFilter: {
         handler() {
            this.pagination.page = 1;
            this.getProposalsList();
         },
         deep: true
      },
      '$route.query': {
         handler() {
            this.checkRouteForCreateProposal();
         },
         deep: true
      },
   },

   methods: {
      async init() {
         await Promise.all([
            this.getVertexUsers(),
            this.getLeadsList(),
         ]);
         this.getProposalsList();
         this.fetchKpiMetrics();
      },

      setQuickTab(tab) {
         this.activeQuickTab = tab;
         this.pagination.page = 1;
         if (tab === 'all') {
            this.statusFilter = ["0", "1", "2", "3", "5"];
         } else if (tab === 'sent') {
            this.statusFilter = ["1", "2"];
         } else if (tab === 'accepted') {
            this.statusFilter = ["3", "6"];
         } else if (tab === 'draft') {
            this.statusFilter = ["0"];
         } else if (tab === 'expired') {
            this.statusFilter = ["5"];
         }
         this.getProposalsList();
      },

      async fetchKpiMetrics() {
         try {
            const res = await this.$axios.get('/proposals?pageSize=500&page=1');
            const allProps = res.data.vertex_proposals || [];
            
            let totalVal = 0;
            let sentCnt = 0;
            let sentVal = 0;
            let acceptedCnt = 0;
            let acceptedVal = 0;
            let expiredCnt = 0;
            let draftCnt = 0;

            allProps.forEach(p => {
               const amt = Number(p.proposal_total_amount) || 0;
               const acceptedAmt = Number(p.proposal_accepted_amount) || 0;
               const status = Number(p.proposal_status);

               totalVal += amt;
               if (status === 0) draftCnt++;
               if (status === 1 || status === 2) {
                  sentCnt++;
                  sentVal += amt;
               }
               if (status === 3 || status === 6) {
                  acceptedCnt++;
                  acceptedVal += (acceptedAmt > 0 ? acceptedAmt : amt);
               }
               if (status === 5) expiredCnt++;
            });

            this.kpiMetrics = {
               totalProposals: res.data.total_items || allProps.length,
               totalValue: totalVal,
               sentCount: sentCnt,
               sentValue: sentVal,
               acceptedCount: acceptedCnt,
               acceptedValue: acceptedVal,
               expiredCount: expiredCnt,
               draftCount: draftCnt,
            };
         } catch (err) {
            console.error("Error fetching KPI metrics for proposals:", err);
         }
      },

      isSorted(field) {
         if (field === 'created_at') return this.sorting.field === 'created_at' || this.sorting.field === 'id';
         if (field === 'proposal_title') return this.sorting.field === 'proposal_title' || this.sorting.field === 'title';
         if (field === 'proposal_status') return this.sorting.field === 'proposal_status' || this.sorting.field === 'status';
         if (field === 'proposal_version') return this.sorting.field === 'proposal_version' || this.sorting.field === 'version';
         if (field === 'proposal_total_amount') return this.sorting.field === 'proposal_total_amount' || this.sorting.field === 'total_amount';
         if (field === 'proposal_accepted_amount') return this.sorting.field === 'proposal_accepted_amount' || this.sorting.field === 'accepted_amount';
         if (field === 'created_by') return this.sorting.field === 'created_by' || this.sorting.field === 'proposal_owner';
         return this.sorting.field === field;
      },

      getSortIcon(field) {
         if (!this.isSorted(field)) {
            return 'fa-solid fa-sort sort-icon-inactive';
         }
         return this.sorting.order === 'asc' ? 'fa-solid fa-sort-up sort-icon-active' : 'fa-solid fa-sort-down sort-icon-active';
      },

      toggleSort(field, defaultOrder = 'asc') {
         if (this.isSorted(field)) {
            this.sorting.order = this.sorting.order === 'asc' ? 'desc' : 'asc';
         } else {
            this.sorting.field = field;
            this.sorting.order = defaultOrder;
         }
         this.pagination.page = 1;
         this.getProposalsList();
      },

      async getProposalsList() {
         this.isLoading = true;
         let apiPath = "/proposals?pageSize=" + this.pagination.pageSize + "&page=" + this.pagination.page;

         if (this.sorting.field) {
            apiPath += "&sort=" + this.sorting.field;
            if (this.sorting.order !== "") {
               apiPath += "&order=" + this.sorting.order;
            }
         }

         if (this.search !== "") {
            apiPath += "&search=" + encodeURIComponent(this.search);
         }

         if (this.statusFilter && this.statusFilter.length > 0) {
            apiPath += "&status=" + this.statusFilter.join(",");
         } else {
            apiPath += "&excludeStatus=4,6";
         }

         if (this.leadFilter && this.leadFilter.length > 0) {
            apiPath += "&lead=" + encodeURIComponent(this.leadFilter.join(","));
         }

         try {
            const response = await this.$axios.get(apiPath);
            this.proposals = response.data.vertex_proposals || [];
            this.pagination.totalItems = response.data.total_items;
            this.pagination.totalPages = Math.ceil(this.pagination.totalItems / this.pagination.pageSize) || 1;
            this.fetchAllProposalContracts();
         } catch (error) {
            this.$toast.error(error.response?.data?.message || "Failed to fetch proposals");
         } finally {
            this.isLoading = false;
         }
      },

      onSearch() {
         this.pagination.page = 1;
         this.debounceFn();
      },

      clearSearch() {
         this.search = '';
         this.pagination.page = 1;
         this.getProposalsList();
      },

      clearAllFiltersAndSearch() {
         this.search = '';
         this.statusFilter = ["0", "1", "2", "3", "5"];
         this.leadFilter = [];
         this.activeQuickTab = 'all';
         this.pagination.page = 1;
         this.getProposalsList();
      },

      removeStatusFilter(val) {
         this.statusFilter = this.statusFilter.filter(v => v !== val);
      },

      removeLeadFilter(val) {
         this.leadFilter = this.leadFilter.filter(v => v !== val);
      },

      getOrganisationDisplayName(proposal) {
         if (proposal.auth_organisation && proposal.auth_organisation.name) {
            return proposal.auth_organisation.name;
         }
         if (proposal.contract_acceptances && proposal.contract_acceptances.length > 0 && proposal.contract_acceptances[0].organisation_name) {
            return proposal.contract_acceptances[0].organisation_name;
         }
         if (proposal.Lead && proposal.Lead.organisation_name) {
            return proposal.Lead.organisation_name;
         }
         return "No Org Name Set";
      },

      getLeadDisplayName(uuid) {
         if (!this.leads) return uuid;
         const lead = this.leads.find(l => l.id === uuid || l.uuid === uuid);
         if (!lead) return uuid;
         return lead.organisation_name || `${lead.first_name} ${lead.last_name}`;
      },

      async getLeadsList() {
         try {
            const res = await this.$axios.get("/leads?pageSize=200");
            this.leads = res.data.leads || [];
         } catch (err) {
            console.error("Failed to load leads", err);
         }
      },

      getVertexAuthUser(id) {
         if (!this.vtxUsers || !Array.isArray(this.vtxUsers)) return "System";
         const user = this.vtxUsers.find(u => u && (u.id === id || u.auth_user_id === id));
         if (user) {
            return `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.name || 'User';
         }
         return id ? `User #${id}` : "System";
      },

      getVertexUsers() {
         this.vtxUsers = this.$store.state.auth.usersWithVertexRoles || [];
      },

      getColorFromName(name) {
         if (!name) return '#64748b';
         let hash = 0;
         for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
         return this.colorPalette[Math.abs(hash) % this.colorPalette.length];
      },

      retrieveUserIcon(fullName) {
         if (!fullName) return "U";
         const parts = fullName.trim().split(' ');
         if (parts.length >= 2) return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
         return parts[0].substring(0, 2).toUpperCase();
      },

      formatCurrency(value) {
         const num = parseFloat(value);
         if (isNaN(num)) return '$0.00';
         return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      },

      isValidDate(date) {
         if (!date) return false;
         const m = moment(date);
         return m.isValid() && m.year() >= 1980;
      },

      formatDate(date) {
         if (!this.isValidDate(date)) return "-";
         return moment(date).format("DD-MMM-YYYY");
      },

      formatTimestamp(date) {
         if (!this.isValidDate(date)) return "-";
         return moment(date).format("DD-MMM-YYYY @ HH:mm:ss");
      },

      previousPage() {
         if (this.pagination.page > 1) this.pagination.page--;
      },

      nextPage() {
         if (this.pagination.page < this.pagination.totalPages) this.pagination.page++;
      },

      getProposalStatusBadgeClass(statusCode) {
         switch (Number(statusCode)) {
            case 1: return 'spark-pill-badge--sent';
            case 2: return 'spark-pill-badge--resent';
            case 3: return 'spark-pill-badge--accepted';
            case 4: return 'spark-pill-badge--declined';
            case 5: return 'spark-pill-badge--expired';
            case 6: return 'spark-pill-badge--completed';
            default: return 'spark-pill-badge--draft';
         }
      },

      getProposalStatusIcon(statusCode) {
         switch (Number(statusCode)) {
            case 1: return 'fa-solid fa-paper-plane';
            case 2: return 'fa-solid fa-rotate-right';
            case 3: return 'fa-solid fa-circle-check';
            case 4: return 'fa-solid fa-circle-xmark';
            case 5: return 'fa-solid fa-calendar-xmark';
            case 6: return 'fa-solid fa-check-double';
            default: return 'fa-solid fa-pen';
         }
      },

      getProposalStatusDotClass(statusCode) {
         switch (Number(statusCode)) {
            case 1:
            case 2: return 'status-dot--teal';
            case 3:
            case 6: return 'status-dot--emerald';
            case 4:
            case 5: return 'status-dot--rose';
            default: return 'status-dot--indigo';
         }
      },

      getStatusText(statusCode) {
         switch (Number(statusCode)) {
            case 1: return "Sent";
            case 2: return "Resent";
            case 3: return "Accepted";
            case 4: return "Declined";
            case 5: return "Expired";
            case 6: return "Completed";
            default: return "Draft";
         }
      },

      getVersionBadgeClass(versionNumber) {
         if (!versionNumber) return 'spark-pill-badge--version-major';
         const clean = String(versionNumber).startsWith('v') ? String(versionNumber).slice(1) : String(versionNumber);
         const parts = clean.split('.');
         if (parts.length >= 2) {
            const minor = parseInt(parts[1], 10);
            if (!isNaN(minor)) {
               return minor > 0 ? 'spark-pill-badge--version-minor' : 'spark-pill-badge--version-major';
            }
         }
         return 'spark-pill-badge--version-major';
      },

      getProposalTooltip(proposal) {
         let titles = [];
         if (proposal.renewed_from_proposal_uuid) titles.push('Re-issued proposal');
         if (proposal.is_extended) titles.push('Expiry date has been extended');
         return titles.join(' | ') || proposal.proposal_title || '';
      },

      isProposalExpired(proposal) {
         if (!proposal.expiry_date) return false;
         const expiry = moment(proposal.expiry_date);
         const now = moment();
         return proposal.proposal_status == 5 || expiry.isBefore(now);
      },

      getSharedEmailsTooltip(sharedUsers) {
         if (!sharedUsers || sharedUsers.length === 0) return "";
         return "Shared with:\n" + sharedUsers.map(u => u.email).join("\n");
      },

      getContractCount(proposalId) {
         return (this.proposalContractsMap[proposalId] || []).length;
      },

      getContractButtonTitle(proposal) {
         const count = this.getContractCount(proposal.id);
         if (count === 0) return 'No contracts linked';
         if (count === 1) return 'View Contract';
         return `View Contracts (${count})`;
      },

      getSendButtonTitle(proposal) {
         if (proposal.proposal_status == 3) return 'Proposal already accepted';
         if (proposal.proposal_status == 4) return 'Proposal declined';
         if (proposal.proposal_status == 6) return 'Proposal completed';
         return 'Send Proposal';
      },

      async handleViewContracts(proposal) {
         const cached = this.proposalContractsMap[proposal.id];
         if (cached && cached.length === 1) {
            this.openContractView(cached[0]);
         } else if (cached && cached.length > 1) {
            this.selectedProposalContracts = cached;
            this.isContractModalOpen = true;
         } else {
            try {
               const res = await this.$axios.get(`/proposals/${proposal.id}/contracts`);
               const contracts = res.data.vertex_contracts || [];
               this.proposalContractsMap = { ...this.proposalContractsMap, [proposal.id]: contracts };
               if (contracts.length === 1) {
                  this.openContractView(contracts[0]);
               } else if (contracts.length > 1) {
                  this.selectedProposalContracts = contracts;
                  this.isContractModalOpen = true;
               } else {
                  this.$toast.info('No contracts are linked to this proposal.');
               }
            } catch (e) {
               this.$toast.error('Failed to load contracts.');
            }
         }
      },

      openContractView(contract) {
         const routeData = this.$router.resolve({ name: 'contract-view', params: { id: contract.id } });
         window.open(routeData.href, '_blank');
      },

      getContractStatusColor(status) {
         switch (status) {
            case 0: return '#6C757D';
            case 1: return '#00A0C1';
            case 2: return '#17A2B8';
            case 3: return '#FFC107';
            case 4: return '#28A745';
            case 5: return '#DC3545';
            case 6: return '#FD7E14';
            case 7: return '#20C997';
            case 8: return '#DC3545';
            case 9: return '#DC3545';
            default: return '#6C757D';
         }
      },

      getContractStatusText(status) {
         switch (status) {
            case 0: return 'Draft';
            case 1: return 'Sent';
            case 2: return 'Viewed';
            case 3: return 'In Review';
            case 4: return 'Executed';
            case 5: return 'Cancelled';
            case 6: return 'Pending Approval';
            case 7: return 'Approved';
            case 8: return 'Declined';
            case 9: return 'Expired';
            default: return 'Draft';
         }
      },

      async fetchAllProposalContracts() {
         if (!this.proposals || this.proposals.length === 0) return;
         const proposalIds = this.proposals.map(p => p.id);
         const promises = proposalIds.map(id =>
            this.$axios.get(`/proposals/${id}/contracts`)
               .then(res => ({ id, contracts: res.data.vertex_contracts || [] }))
               .catch(() => ({ id, contracts: [] }))
         );
         const results = await Promise.all(promises);
         const map = {};
         results.forEach(r => { map[r.id] = r.contracts; });
         this.proposalContractsMap = map;
      },

      openSendModal() {
         this.sendProposalForm.show();
      },

      openProposalInNewTab(id) {
         const routeData = this.$router.resolve({ name: 'proposal-view', params: { id: id } });
         window.open(routeData.href, '_blank');
      },

      viewClientProposal(proposal) {
         if (proposal && proposal.unique_key && proposal.internal_key) {
            const routeData = this.$router.resolve({ name: 'clientProposal', params: { unique_key: proposal.unique_key, access_key: proposal.internal_key } });
            window.open(routeData.href, '_blank');
         } else {
            this.$toast.error("Could not generate client link. Proposal data is missing required keys.");
         }
      },

      onButtonClick(action, data) {
         if (action === "send") {
            this.proposal_client_email = data.Lead?.email || '';
            this.proposal_id = data.id;
            this.proposal_title = data.proposal_title;
         } else if (action === "view-edit") {
            this.$router.push({ name: 'proposal-view', params: { id: data } });
         } else if (action === "delete") {
            this.deleteProposal(data);
         } else if (action === "view-client") {
            this.viewClientProposal(data);
         } else if (action === "view-contracts") {
            this.handleViewContracts(data);
         } else if (action === "activity-log") {
            this.$refs.proposalActivityRef.init(data);
            this.activityLogModalInstance.show();
         }
      },

      checkRouteForCreateProposal() {
         const q = this.$route.query;
         if (q && (q.create === 'true' || q.create === true || q.org_id || q.lead_id || q.contact_id || q.contact_uuid || q.contact_email)) {
            this.$nextTick(() => {
               if (this.createProposalModalInstance && this.$refs.createProposalRef) {
                  this.openCreateProposalModal({
                     orgId: q.org_id || '',
                     leadId: q.lead_id || '',
                     contactId: q.contact_id || '',
                     contactUuid: q.contact_uuid || '',
                     contactEmail: q.contact_email || ''
                  });
               }
            });
         }
      },

      openCreateProposalModal(options = null) {
         this.selectedLeadID = "";
         this.isLeadSelected = false;
         if (this.$refs.createProposalRef) {
            this.$refs.createProposalRef.init("", options);
         }
         if (this.createProposalModalInstance) {
            this.createProposalModalInstance.show();
         }
      },

      closeCreateProposalModal() {
         if (this.createProposalModalInstance) {
            this.createProposalModalInstance.hide();
         }
         if (this.$route.query && (this.$route.query.create || this.$route.query.org_id || this.$route.query.lead_id || this.$route.query.contact_id || this.$route.query.contact_uuid || this.$route.query.contact_email)) {
            this.$router.replace({ path: '/proposals-v2' });
         }
         this.getProposalsList();
         this.fetchKpiMetrics();
      },

      handleLeadChanged(lead) {
         this.isLeadSelected = !!lead;
      },

      async deleteProposal(id) {
         this.pendingAction = 'deleteProposal';
         this.pendingActionData = id;
         this.confirmModalTitle = 'Delete Proposal';
         this.confirmModalMessage = 'Are you sure you want to delete this proposal?';
         this.showConfirmModal = true;
      },

      async refreshDraftPrices() {
         this.pendingAction = 'refreshDraftPrices';
         this.pendingActionData = null;
         this.confirmModalTitle = 'Refresh Draft Prices';
         this.confirmModalMessage = 'Are you sure you want to refresh prices for all draft proposals? This will update item prices to current Auth/Master rates.';
         this.showConfirmModal = true;
      },

      async onConfirmAction() {
         const action = this.pendingAction;
         const data = this.pendingActionData;
         this.pendingAction = null;
         this.pendingActionData = null;

         if (action === 'deleteProposal') {
            this.isLoading = true;
            try {
               const res = await this.$axios.delete("/proposals/" + data);
               if (res.data.success) {
                  this.$toast.success(res.data.message);
                  this.getProposalsList();
                  this.fetchKpiMetrics();
               } else {
                  this.$toast.error(res.data.message);
               }
            } catch (err) {
               this.$toast.error(err.response?.data?.message || "Failed to delete proposal");
            } finally {
               this.isLoading = false;
            }
         } else if (action === 'refreshDraftPrices') {
            this.isLoading = true;
            try {
               const response = await this.$axios.post("/proposals/draft-price-refresh");
               if (response.data.success) {
                  this.$toast.success(response.data.message || "Price refresh initiated in the background.");
               } else {
                  this.$toast.error(response.data.message || "Failed to start price refresh.");
               }
            } catch (error) {
               this.$toast.error(error.response?.data?.message || "An error occurred while starting the price refresh.");
            } finally {
               this.isLoading = false;
            }
         }
      }
   }
}
</script>

<style scoped>
/* ── Proposals V2 Workspace Styling (Exact match to Leads V2) ── */
.proposals-page-v2 {
   color: #1e293b;
   font-family: inherit;
}

.fs-xs { font-size: 0.75rem; }
.fs-sm { font-size: 0.85rem; }
.fw-500 { font-weight: 500; }

.bg-teal { background-color: #00A0C1 !important; }
.text-teal { color: #00A0C1 !important; }
.bg-teal-soft { background-color: rgba(0, 160, 193, 0.1) !important; }

.text-blue { color: #3b82f6 !important; }
.bg-blue-soft { background-color: rgba(59, 130, 246, 0.1) !important; }

.text-emerald { color: #10b981 !important; }
.bg-emerald-soft { background-color: rgba(16, 185, 129, 0.1) !important; }

.text-amber { color: #d97706 !important; }
.text-amber-muted { color: #b45309 !important; font-size: 0.75rem; }
.bg-amber-soft { background-color: rgba(245, 158, 11, 0.1) !important; }

.text-indigo { color: #6366f1 !important; }
.bg-indigo-soft { background-color: rgba(99, 102, 241, 0.1) !important; }

.text-rose { color: #e11d48 !important; }
.bg-rose-soft { background-color: rgba(239, 68, 68, 0.1) !important; }

.bg-primary-soft {
   background-color: rgba(0, 160, 193, 0.1) !important;
   color: #008298 !important;
}

/* ── Page Header Styles ── */
.ms-page-header {
   margin-bottom: 20px;
   padding: 18px 22px;
   background: linear-gradient(135deg, #ffffff 0%, #f4f9fb 100%);
   border: 1px solid rgba(0, 130, 152, 0.15);
   border-left: 5px solid #008298;
   border-radius: 12px;
   box-shadow: 0 4px 16px rgba(0, 78, 90, 0.04);
}

.ms-page-header__content {
   display: flex;
   align-items: center;
   gap: 16px;
}

.ms-page-header__icon {
   width: 44px;
   height: 44px;
   border-radius: 10px;
   background: rgba(0, 130, 152, 0.08);
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 1.15rem;
   color: #008298;
}

.ms-page-header__title {
   font-size: 1.45rem;
   font-weight: 800;
   color: #00363f;
   margin: 0;
   letter-spacing: -0.3px;
}

.ms-page-header__breadcrumb {
   font-size: 0.76rem;
   color: #557d84;
   font-weight: 700;
   text-transform: uppercase;
   letter-spacing: 0.8px;
   margin-bottom: 4px;
}

.ms-breadcrumb-sep {
   margin: 0 6px;
   color: rgba(0, 130, 152, 0.3);
}

.modern-action-btn {
   height: 38px;
   padding: 0 16px;
   font-size: 0.85rem;
   font-weight: 600;
   border-radius: 8px;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   transition: all 0.2s ease;
}

.btn-ghost-secondary {
   background: #ffffff;
   border: 1px solid #cbd5e1;
   color: #334155;
}
.btn-ghost-secondary:hover:not(:disabled) {
   background: #f8fafc;
   border-color: #00A0C1;
   color: #00A0C1;
}

/* ── KPI Metric Cards ── */
/* ── Modern Table Card & Workspace ── */
.modern-table-card {
   border-radius: 14px;
   background: #ffffff;
}

/* ── Modern Tabs ── */
.modern-tabs {
   display: flex;
   gap: 6px;
   flex-wrap: wrap;
}

.modern-tab {
   background: #f8fafc;
   border: 1px solid #e2e8f0;
   border-radius: 8px;
   padding: 6px 14px;
   font-size: 0.85rem;
   font-weight: 600;
   color: #64748b;
   cursor: pointer;
   transition: all 0.2s ease;
   display: inline-flex;
   align-items: center;
}

.modern-tab:hover {
   background: #f1f5f9;
   color: #334155;
}

.modern-tab.active {
   background: #00A0C1;
   color: #ffffff;
   border-color: #00A0C1;
   box-shadow: 0 2px 6px rgba(0, 160, 193, 0.25);
}

.modern-tab.active i {
   color: #ffffff !important;
}

.modern-tab.active .badge {
   background-color: rgba(255, 255, 255, 0.25) !important;
   color: #ffffff !important;
}

/* ── Search & Filter Omnibar ── */
.modern-search-wrapper {
   position: relative;
   display: flex;
   align-items: center;
}

.search-icon {
   position: absolute;
   left: 12px;
   color: #94a3b8;
   font-size: 0.85rem;
   pointer-events: none;
}

.modern-search-input {
   height: 38px;
   border-radius: 8px;
   border: 1px solid #cbd5e1;
   padding-left: 36px;
   padding-right: 32px;
   font-size: 0.85rem;
   transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.modern-search-input:focus {
   border-color: #00A0C1;
   box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.15);
}

.btn-clear-search {
   position: absolute;
   right: 8px;
   background: none;
   border: none;
   color: #94a3b8;
   font-size: 0.85rem;
   cursor: pointer;
   padding: 4px;
}

.btn-clear-search:hover { color: #475569; }

.modern-filter-btn {
   height: 38px;
   border-radius: 8px;
   border: 1px solid #cbd5e1;
   background: #ffffff;
   color: #334155;
   font-weight: 600;
   font-size: 0.85rem;
   padding: 0 12px;
   display: inline-flex;
   align-items: center;
   transition: all 0.2s ease;
}

.modern-filter-btn:hover,
.modern-filter-btn[aria-expanded="true"] {
   border-color: #00A0C1;
   background: #f8fafc;
   color: #00A0C1;
}

.modern-filter-btn.has-active {
   background: #e6f7fa;
   border-color: #00A0C1;
   color: #008298;
}

.filter-count-badge {
   background: #00A0C1;
   color: #ffffff;
   font-size: 0.7rem;
   font-weight: 700;
   padding: 1px 6px;
   border-radius: 8px;
}

.modern-select {
   border-radius: 8px;
   border: 1px solid #cbd5e1;
   height: 34px;
   font-size: 0.8rem;
   font-weight: 500;
   color: #334155;
}

.modern-select:focus {
   border-color: #00A0C1;
   box-shadow: 0 0 0 2px rgba(0, 160, 193, 0.15);
}

.filter-chip {
   display: inline-flex;
   align-items: center;
   gap: 6px;
   background: #e6f7fa;
   color: #008298;
   border: 1px solid #b3e5ef;
   padding: 3px 10px;
   border-radius: 16px;
   font-size: 0.75rem;
   font-weight: 600;
}

.filter-chip i {
   font-size: 0.7rem;
   transition: color 0.15s;
}

.filter-chip i:hover { color: #ef4444; }

/* ── Modern Table Data Grid ── */
.modern-table-wrapper {
   border: 1px solid #e2e8f0;
   border-radius: 10px;
   background: #ffffff;
}

.modern-table {
   border-collapse: separate;
   border-spacing: 0;
   width: 100%;
}

.modern-table thead th {
   background-color: #f8fafc;
   color: #475569;
   font-weight: 700;
   font-size: 0.78rem;
   text-transform: uppercase;
   letter-spacing: 0.5px;
   padding: 12px 16px;
   border-bottom: 1.5px solid #e2e8f0;
   white-space: nowrap !important;
}

/* ── Sortable Column Headers ── */
.sortable-th {
   cursor: pointer;
   user-select: none;
   transition: background-color 0.15s ease, color 0.15s ease;
}

.sortable-th:hover {
   background-color: #f1f5f9 !important;
   color: #00A0C1 !important;
}

.sortable-th.sorted-th {
   color: #00A0C1 !important;
}

.sort-icon-inactive {
   color: #cbd5e1;
   font-size: 0.72rem;
   margin-left: 4px;
   opacity: 0.45;
   transition: opacity 0.15s ease, color 0.15s ease;
}

.sortable-th:hover .sort-icon-inactive {
   opacity: 0.85;
   color: #94a3b8;
}

.sort-icon-active {
   color: #00A0C1 !important;
   font-size: 0.75rem;
   margin-left: 4px;
}

.modern-row {
   cursor: pointer;
   transition: background-color 0.15s ease;
}

.modern-row:hover {
   background-color: #f8fafc;
}

.modern-row td {
   padding: 12px 16px !important;
   border-bottom: 1px solid #f1f5f9;
   vertical-align: middle;
}

.proposal-org-name {
   font-weight: 700;
   font-size: 0.92rem;
   color: #0f172a;
}

.modern-row:hover .proposal-org-name {
   color: #00A0C1;
}

.proposal-sub-title {
   font-size: 0.8rem;
}

.user-initials-icon-sm {
   width: 28px;
   height: 28px;
   border-radius: 50%;
   color: #ffffff;
   font-size: 0.75rem;
   font-weight: 700;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   flex-shrink: 0;
}

/* ── Spark Soft Pill Badges (Matching Leads & Contracts V2) ── */
.spark-pill-badge {
   display: inline-flex;
   align-items: center;
   justify-content: center;
   gap: 6px;
   padding: 4px 10px;
   border-radius: 14px;
   font-size: 0.8125rem;
   font-weight: 600;
   white-space: nowrap;
   line-height: 1.35;
   letter-spacing: 0.2px;
   text-decoration: none;
   vertical-align: middle;
   border: 1px solid transparent;
   transition: all 0.15s ease-in-out;
}

.spark-pill-badge i {
   font-size: 0.78rem;
}

.spark-pill-badge--sent { background: #ecfeff; color: #008298; border-color: #cffafe; }
.spark-pill-badge--resent { background: #e0f2fe; color: #0369a1; border-color: #bae6fd; }
.spark-pill-badge--accepted { background: #ecfdf5; color: #059669; border-color: #a7f3d0; }
.spark-pill-badge--completed { background: #ecfdf5; color: #047857; border-color: #6ee7b7; }
.spark-pill-badge--declined { background: #fee2e2; color: #b91c1c; border-color: #fca5a5; }
.spark-pill-badge--expired { background: #fee2e2; color: #b91c1c; border-color: #fca5a5; }
.spark-pill-badge--draft { background: #f8fafc; color: #475569; border-color: #e2e8f0; }
.spark-pill-badge--linked { background: #eff6ff; color: #2563eb; border-color: #bfdbfe; font-size: 0.7rem; padding: 2px 7px; }
.spark-pill-badge--version-major { background: #ecfdf5 !important; color: #059669 !important; border: 1px solid #a7f3d0 !important; }
.spark-pill-badge--version-minor { background: #fff7ed !important; color: #c2410c !important; border: 1px solid #fed7aa !important; }
.spark-pill-badge--version-default { background: #f1f5f9 !important; color: #475569 !important; border: 1px solid #cbd5e1 !important; }

/* ── Streamlined Action Buttons (Matching LeadV2) ── */
.btn-action-send {
   height: 32px;
   padding: 0 12px;
   font-size: 0.8rem;
   font-weight: 600;
   border-radius: 6px;
   background: #e6f7fa;
   color: #008298;
   border: 1px solid rgba(0, 160, 193, 0.2);
   display: inline-flex;
   align-items: center;
   transition: all 0.15s ease;
}

.btn-action-send:hover:not(:disabled) {
   background: #00A0C1;
   color: #ffffff;
   box-shadow: 0 2px 6px rgba(0, 160, 193, 0.25);
}

.btn-action-send:disabled {
   opacity: 0.4;
   cursor: not-allowed;
}

.btn-more-actions {
   width: 34px;
   height: 34px;
   min-width: 34px;
   min-height: 34px;
   padding: 0;
   border-radius: 50% !important;
   background: #f8fafc;
   border: 1px solid #e2e8f0;
   color: #64748b;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   font-size: 0.85rem;
   cursor: pointer;
   transition: all 0.2s ease-in-out;
}

.btn-more-actions:hover {
   background: #f1f5f9;
   color: #00A0C1;
   border-color: #cbd5e1;
   transform: translateY(-1px);
   box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.modern-action-dropdown {
   min-width: 220px;
   border-radius: 10px;
}

.modern-action-dropdown .dropdown-item {
   padding: 8px 16px;
   font-size: 0.85rem;
   font-weight: 500;
   color: #334155;
   transition: all 0.15s ease;
}

.modern-action-dropdown .dropdown-item:hover {
   background-color: #f8fafc;
   color: #00A0C1;
}

/* ── Circular Action Buttons ── */
.action-btn-circle {
   width: 34px;
   height: 34px;
   min-width: 34px;
   min-height: 34px;
   padding: 0;
   border-radius: 50% !important;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   font-size: 0.85rem;
   border: none;
   cursor: pointer;
   transition: all 0.2s ease-in-out;
   text-decoration: none;
   position: relative;
}

.action-btn-circle:hover:not(:disabled) {
   transform: translateY(-1px);
}

.action-btn-circle:disabled,
.action-btn-circle.disabled {
   opacity: 0.35;
   cursor: not-allowed;
   pointer-events: none;
   transform: none !important;
   box-shadow: none !important;
}

.action-btn-circle--primary,
.action-btn-circle--proposal,
.action-btn-circle--send,
.action-btn-circle--edit {
   background-color: #e6f7fa;
   color: #00A0C1;
}

.action-btn-circle--primary:hover:not(:disabled),
.action-btn-circle--proposal:hover:not(:disabled),
.action-btn-circle--send:hover:not(:disabled),
.action-btn-circle--edit:hover:not(:disabled) {
   background-color: #00A0C1;
   color: #ffffff;
   box-shadow: 0 4px 12px rgba(0, 160, 193, 0.25);
}

.action-btn-circle--sync {
   background-color: #f0fdfa;
   color: #0d9488;
}

.action-btn-circle--sync:hover:not(:disabled) {
   background-color: #0d9488;
   color: #ffffff;
   box-shadow: 0 4px 10px rgba(13, 148, 136, 0.3);
}

.action-btn-circle--restore {
   background-color: #fffbeb;
   color: #f59e0b;
}

.action-btn-circle--restore:hover:not(:disabled) {
   background-color: #f59e0b;
   color: #ffffff;
   box-shadow: 0 4px 10px rgba(245, 158, 11, 0.3);
}

.action-btn-circle--unsent {
   background-color: #fffbeb;
   color: #b45309;
}

.action-btn-circle--unsent:hover:not(:disabled) {
   background-color: #f59e0b;
   color: #ffffff;
   box-shadow: 0 4px 10px rgba(245, 158, 11, 0.3);
}

.action-icon-btn--danger:hover:not(:disabled) {
   background: #fee2e2 !important;
   color: #ef4444 !important;
   border-color: #fca5a5 !important;
}

.contract-count-bubble {
   position: absolute;
   top: -4px;
   right: -4px;
   background: #6366f1;
   color: #ffffff;
   font-size: 0.65rem;
   font-weight: 800;
   width: 16px;
   height: 16px;
   border-radius: 50%;
   display: flex;
   align-items: center;
   justify-content: center;
   border: 1.5px solid #ffffff;
}

/* Status dots */
.status-dot {
   width: 8px;
   height: 8px;
   border-radius: 50%;
}
.status-dot-xs {
   width: 6px;
   height: 6px;
   border-radius: 50%;
   display: inline-block;
}
.status-dot--teal { background-color: #00A0C1; }
.status-dot--emerald { background-color: #10b981; }
.status-dot--amber { background-color: #f59e0b; }
.status-dot--indigo { background-color: #6366f1; }
.status-dot--rose { background-color: #ef4444; }

.modern-checkbox-label {
   display: flex;
   align-items: center;
   gap: 8px;
   padding: 6px 8px;
   border-radius: 6px;
   cursor: pointer;
   font-size: 0.8125rem;
   color: #334155;
   transition: background 0.15s ease;
   user-select: none;
   position: relative;
   margin-bottom: 0;

   &:hover {
      background: #f1f5f9;
   }

   input[type="checkbox"] {
      position: absolute;
      opacity: 0;
      cursor: pointer;
      height: 0;
      width: 0;
   }

   .custom-chk {
      width: 16px;
      height: 16px;
      background-color: #ffffff;
      border: 1.5px solid #cbd5e1;
      border-radius: 4px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      transition: all 0.15s ease;
      flex-shrink: 0;
      position: relative;
   }

   &:hover input[type="checkbox"]:not(:checked) ~ .custom-chk {
      border-color: #00A0C1;
   }

   input[type="checkbox"]:checked ~ .custom-chk {
      background-color: #00A0C1;
      border-color: #00A0C1;
   }

   input[type="checkbox"]:checked ~ .custom-chk::after {
      content: "";
      width: 4px;
      height: 8px;
      border: solid #ffffff;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
      margin-bottom: 1px;
   }

   input[type="checkbox"]:focus ~ .custom-chk {
      box-shadow: 0 0 0 2px rgba(0, 160, 193, 0.2);
   }
}

/* ── Pagination Modern ── */
.pagination-modern {
   display: inline-flex;
   align-items: center;
   border: 1px solid #cbd5e1;
   border-radius: 8px;
   background: #ffffff;
   overflow: hidden;
}

.pagination-arrow {
   background: transparent;
   border: none;
   padding: 6px 12px;
   color: #00A0C1;
   cursor: pointer;
   font-size: 0.8rem;
   transition: background 0.15s ease;
}

.pagination-arrow:hover:not(:disabled) {
   background: #f1f5f9;
}

.pagination-arrow:disabled {
   color: #cbd5e1;
   cursor: not-allowed;
}

.pagination-label {
   font-size: 0.8rem;
   font-weight: 500;
   color: #64748b;
   padding: 0 8px;
   border-left: 1px solid #f1f5f9;
   border-right: 1px solid #f1f5f9;
}

/* ── Contract Modal Overlays ── */
.contract-overlay {
   position: fixed;
   top: 0; left: 0; right: 0; bottom: 0;
   background: rgba(15, 23, 42, 0.45);
   backdrop-filter: blur(4px);
   z-index: 1050;
   display: flex;
   align-items: center;
   justify-content: center;
}

.contract-select-modal {
   background: #ffffff;
   border-radius: 16px;
   width: 100%;
   max-width: 480px;
   padding: 24px;
   box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.contract-select-header {
   display: flex;
   justify-content: space-between;
   align-items: center;
   margin-bottom: 16px;
}

.contract-select-title {
   font-weight: 700;
   font-size: 1.15rem;
   margin: 0;
}

.contract-select-close {
   background: transparent;
   border: none;
   font-size: 1.5rem;
   color: #94a3b8;
   cursor: pointer;
}

.contract-card {
   border: 1px solid #e2e8f0;
   border-radius: 10px;
   padding: 12px 16px;
   margin-bottom: 10px;
   cursor: pointer;
   transition: all 0.15s ease;
}

.contract-card:hover {
   border-color: #00A0C1;
   background: #f8fafc;
   transform: translateX(4px);
}

.contract-card-name {
   font-weight: 700;
   margin-bottom: 4px;
   color: #0f172a;
}

.contract-card-detail {
   font-size: 0.8rem;
   margin-bottom: 2px;
   color: #64748b;
}

/* Modal sizing & backdrop */
.blur-backdrop {
   position: fixed;
   top: 0;
   left: 0;
   right: 0;
   bottom: 0;
   backdrop-filter: blur(5px);
   background-color: rgba(0, 0, 0, 0.4);
}
.modal-md-width {
   max-width: 900px !important;
   width: 90% !important;
   margin-left: auto !important;
   margin-right: auto !important;
   position: relative;
   z-index: 2;
   transition: max-width 0.3s ease-in-out;
}
.modal-custom-width {
   max-width: 95% !important;
   width: 95% !important;
   margin-left: auto !important;
   margin-right: auto !important;
   position: relative;
   z-index: 2;
   transition: max-width 0.3s ease-in-out;
}
.close-button-custom {
   position: absolute;
   top: 16px;
   right: 18px;
   background: none;
   border: none;
   font-size: 1.75rem;
   line-height: 1;
   color: #64748b;
   cursor: pointer;
   z-index: 10;
}

/* ── Modern Activity Modal Styling ── */
.modal-blur {
   backdrop-filter: blur(4px);
}

.modern-modal-content {
   border-radius: 16px;
   border: none;
   box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
   overflow: hidden;
   background: #ffffff;
}

.modern-activity-modal-header {
   display: flex;
   align-items: center;
   justify-content: space-between;
   padding: 18px 24px;
   background: #ffffff;
   border-bottom: 1px solid #e2e8f0;
}

.modal-header-icon-badge {
   width: 40px;
   height: 40px;
   border-radius: 10px;
   background: rgba(0, 160, 193, 0.1);
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 1.1rem;
   color: #00A0C1;
   flex-shrink: 0;
}

.modal-header-icon-badge i {
   color: #00A0C1 !important;
}

.modern-activity-modal-header .modal-title {
   font-size: 1.05rem;
   color: #1e293b;
}

.modern-activity-modal-header .modal-subtitle {
   font-size: 0.8rem;
   color: #64748b;
}

.modern-modal-close-btn {
   width: 32px;
   height: 32px;
   border-radius: 8px;
   border: none;
   background: #f1f5f9;
   color: #64748b;
   display: flex;
   align-items: center;
   justify-content: center;
   cursor: pointer;
   transition: all 0.15s ease;
}

.modern-modal-close-btn:hover {
   background: #e2e8f0;
   color: #1e293b;
}

.modern-activity-modal-body {
   background: #ffffff;
   max-height: 70vh;
   overflow-y: auto;
}

.modern-activity-modal-footer {
   padding: 12px 24px;
   background: #f8fafc;
   border-top: 1px solid #e2e8f0;
   display: flex;
   justify-content: flex-end;
}
</style>
